import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// Template policies — owner should review and customise before going live.
const POLICIES = {
  shipping: {
    title: "Shipping Information",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "Delivery areas",
        p: "We currently ship within the United Kingdom. If you are ordering from outside the UK, please contact us at hello@aliver.co.uk before placing your order.",
      },
      {
        h: "Processing time",
        p: "Orders are processed within 1–2 business days. You will receive a confirmation email once your order has been dispatched.",
      },
      {
        h: "Delivery time",
        p: "Standard UK delivery typically takes 2–4 business days from dispatch. Subscription orders are dispatched each month around your renewal date.",
      },
      {
        h: "Shipping costs",
        p: "Any shipping costs are shown clearly at checkout before you pay. From time to time we may offer free shipping promotions.",
      },
      {
        h: "Delays & issues",
        p: "If your order has not arrived within 10 business days of dispatch, contact us at hello@aliver.co.uk and we will investigate with the carrier and make it right.",
      },
    ],
  },
  returns: {
    title: "Returns & Refunds",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "14-day change of mind",
        p: "Under UK consumer law you have 14 days from receiving your order to change your mind. For hygiene reasons, we can only accept returns of products that are unopened and in their original sealed packaging.",
      },
      {
        h: "Faulty or damaged items",
        p: "If your product arrives damaged or faulty, contact us at hello@aliver.co.uk within 30 days with a photo of the issue and we will arrange a replacement or full refund.",
      },
      {
        h: "How to start a return",
        p: "Email hello@aliver.co.uk with your order number and reason for return. We will reply with return instructions. Please do not send items back without contacting us first.",
      },
      {
        h: "Refunds",
        p: "Once we receive and inspect your return, refunds are issued to your original payment method within 5–10 business days. Original shipping costs are non-refundable unless the item was faulty.",
      },
      {
        h: "Subscriptions",
        p: "Subscription renewals that have already been dispatched follow the same returns policy. To stop future renewals, cancel your subscription as described in our Subscription Terms.",
      },
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "What we collect",
        p: "When you place an order we collect your name, email address, delivery address and payment details (processed securely by our payment provider, Stripe — we never see or store your full card details).",
      },
      {
        h: "How we use it",
        p: "We use your information to fulfil your order, manage your subscription, send order updates, and respond to your enquiries. We do not sell your personal data to anyone.",
      },
      {
        h: "Marketing",
        p: "We only send marketing emails if you have opted in, and you can unsubscribe at any time using the link in any email.",
      },
      {
        h: "Cookies",
        p: "Our site uses essential cookies to operate the checkout and remember your preferences. No advertising trackers are used without your consent.",
      },
      {
        h: "Your rights",
        p: "Under UK GDPR you can request a copy of your data, ask us to correct or delete it, or withdraw consent at any time. Email hello@aliver.co.uk and we will respond within 30 days.",
      },
    ],
  },
  terms: {
    title: "Terms & Conditions",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "The basics",
        p: "By purchasing from this website you agree to these terms. We sell cosmetic skincare products for external use only. Our products are not intended to diagnose, treat, cure or prevent any medical condition.",
      },
      {
        h: "Product use",
        p: "Always patch-test new skincare products on a small area of skin first. Discontinue use and consult a doctor if irritation occurs. Individual experiences with cosmetic products may vary.",
      },
      {
        h: "Pricing & payment",
        p: "Prices are shown in GBP and include any applicable taxes, which are calculated at checkout. Payment is taken securely at the time of order via our payment provider, Stripe.",
      },
      {
        h: "Intellectual property",
        p: "All content on this site — including images, text and branding — belongs to us and may not be reused without permission.",
      },
      {
        h: "Liability",
        p: "Nothing in these terms limits your statutory rights as a UK consumer. Our liability is limited to the purchase price of the product except where the law says otherwise.",
      },
    ],
  },
  "subscription-terms": {
    title: "Subscription Terms",
    updated: "Last updated: September 2026",
    sections: [
      {
        h: "How it works",
        p: "Subscribe & Save costs £14.99 per month. Each billing cycle we charge your saved payment method and dispatch a fresh 60-patch pack to your delivery address.",
      },
      {
        h: "Billing",
        p: "Your first payment is taken at checkout. Renewals are charged automatically each month on or around the same date until you cancel.",
      },
      {
        h: "Cancelling",
        p: "You can cancel at any time — there is no minimum commitment. Email hello@aliver.co.uk from the address used at checkout and we will cancel your subscription and confirm by email. Cancellations take effect from the next billing date; orders already dispatched are not refunded but may be returned under our Returns & Refunds policy.",
      },
      {
        h: "Changes",
        p: "If the subscription price or contents ever change, we will notify you by email at least 14 days before your next billing date, and you may cancel before the change applies.",
      },
      {
        h: "Failed payments",
        p: "If a renewal payment fails, we will retry over the following days and pause deliveries until payment succeeds. If payment cannot be collected, the subscription is cancelled automatically.",
      },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const policy = POLICIES[slug] || POLICIES.terms;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <main className="min-h-screen bg-cream" data-testid="legal-page">
      <header className="border-b border-champagne/70 glass-nav sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="legal-logo" className="font-display text-2xl tracking-[0.18em] text-gold-deep">
            ALIVER
          </Link>
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-ink transition-colors duration-300"
          >
            <ArrowLeft size={15} strokeWidth={1.5} /> Back to shop
          </Link>
        </div>
      </header>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-16 md:py-24">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-gold">ALIVER Policies</p>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-ink" data-testid="legal-title">
          {policy.title}
        </h1>
        <p className="mt-3 text-xs text-[#8C8C8C]">{policy.updated}</p>
        <div className="mt-12 space-y-10">
          {policy.sections.map((s, i) => (
            <section key={s.h} data-testid={`legal-section-${i}`}>
              <h2 className="font-display text-2xl text-ink">{s.h}</h2>
              <p className="mt-3 text-[15px] text-[#5C5C5C] leading-relaxed">{s.p}</p>
            </section>
          ))}
        </div>
        <div className="mt-16 rounded-2xl border border-champagne/80 bg-white p-7">
          <p className="text-sm text-[#5C5C5C] leading-relaxed">
            Questions about this policy? Email us at{" "}
            <a href="mailto:hello@aliver.co.uk" className="font-semibold text-ink underline decoration-gold-light underline-offset-2">
              hello@aliver.co.uk
            </a>{" "}
            and we will be happy to help.
          </p>
        </div>
      </article>
    </main>
  );
}
